import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { Map as TLMDMap } from '../map/map';
import { Timeline } from '../timeline/timeline';
import { Drawer } from '../panel/cabinets';
import { filter } from 'rxjs/operators';
import { eventObserver, delegateObserver, parent } from '../dom/dom';
import { xhrPromise, Method, ResponseType } from '../xhr/xhr';
import { SpatialBounds } from '../data-source/types';
import { HysplitViewer, HysplitViewerOptions } from '../hysplit/hysplit-viewer';
import { Hysplit, HysplitOptions, HysplitResult, ValueClassification } from '../hysplit/hysplit';
import { getPaletteColor, colorToString } from '../util/color';
import { ObservationDataSource } from './observation';
import { ObservationContainer, CallbackDefinition, CallbackDataProperties, ObservationDatum } from './container-observation';

interface HysplitDataProperties extends CallbackDataProperties {
	project?: string,
	result?: {
		id: string,
		mame: string,
		mimeType: string,
		size?: number
	}
}

interface HysplitCallbackDefinition extends CallbackDefinition {
	parameters: {
		hpc: string,
		model: string,
		translation?: { dLat: number, dLng: number }
	},
	viewerOptions: HysplitViewerOptions,
	renderOptions: HysplitOptions
}

export class ObservationHysplitVisualizer implements Visualizer {

	private controls: { [key: string]: VisualizerControl };
	private inspectionContainer: ObservationContainer;
	private source: ObservationDataSource;
	private callbackDefinition: HysplitCallbackDefinition;
	private hysplitViewer: HysplitViewer;

	public constructor(private visualizerKey: string, private obsType: string, private drawer: Drawer) {
		this.inspectionContainer = <ObservationContainer>this.drawer.getContainer();
		this.source = this.inspectionContainer.getDataSource();
		let typeDefinition = this.inspectionContainer.getDefinition();

		this.callbackDefinition = <HysplitCallbackDefinition>typeDefinition.callbacks.filter(callbackDefinition => callbackDefinition.processor === 'hysplit')[0];

		this.hysplitViewer = this.callbackDefinition ? new HysplitViewer(this.callbackDefinition.viewerOptions) : undefined;
		this.subscribeHandlers();
	}

	public release(): void {
		this.setHysplitResult(undefined);
	}

	public key(): string {
		return this.visualizerKey + '_' + this.type();
	}

	public type(): string {
		return this.obsType;
	}

	public setControls(controls: { [key: string]: VisualizerControl }): Visualizer {
		this.controls = controls;
		return this;
	}

	public setTimeline(timeline: Timeline): void {
		this.hysplitViewer.setTimeline(timeline);
	}

	public setMap(map: TLMDMap): void {
		this.hysplitViewer.setMap(map);
	}

	public updateSelection(): void {
		//    this.updateHysplit();
	}

	public refresh(level: VisualizationUpdateLevel): void {
		if (level === VisualizationUpdateLevel.settings || level === VisualizationUpdateLevel.selection) {
			this.updateHysplit();
		}
	}


	public getSpatialCoordinate(): SpatialBounds {
		if (this.hysplitViewer) {
			return this.hysplitViewer.getSpatialCoordinate();
		}
		return undefined;

	}

	public getTemporalCoordinate(): any {
		if (this.hysplitViewer) {
			return this.hysplitViewer.getTemporalCoordinate();
		}
		return undefined;
	}


	private async updateHysplit(): Promise<void> {
		let dataList = this.source.getData()[this.type()];
		let selectionSize = dataList.getSelectionSize();
		let datum: ObservationDatum = selectionSize === 1
			? <ObservationDatum>dataList.getSelection()[0]
			: undefined;

		if (datum && datum.callbacks && datum.callbacks.hysplit) {
			let callbackData = <HysplitDataProperties>datum.callbacks.hysplit;

			//test if we the viewer is active
			if (this.controls && this.controls[this.type()] && this.controls[this.type()].enabled(this.key())) {
				//hysplit visualization is active

				const originalStatus = callbackData.status;
				let status = callbackData.status;

				const statusSignalColor = this.getStatusIndicatorColorClass(status);

				//console.log(`originalStatus=${originalStatus}, originalStatusSignalColor=${statusSignalColor}, callBackData=`, callbackData);

				if (statusSignalColor === 'tlmd-blue') {//i.e. still running
					//still running according to the status but lets check if the calculations might have stalled
					const statusAge = Date.now() - datum.version;
					if (callbackData.status === 'fetching' && statusAge > 10000) {
						//should have fetched the result by in 10 seconds
						status = 'calculated';
					} else if (statusAge > 300000) {
						//should have finished calculations in 5 minutes
						status = 'retry';
						callbackData.retry = `/${this.inspectionContainer.getApplication().getDomain()}/observation/callback/hysplit/${datum.id}?retry=true`;
					}
				}
				if (originalStatus === 'ready') {
					//console.log('Testing for actual results');
					if (!callbackData.result || !callbackData.result.id) {
						status == 'retry';
						callbackData.retry = `/${this.inspectionContainer.getApplication().getDomain()}/observation/callback/hysplit/${datum.id}?retry=true`;
					}
				}
				//console.log(`status=${status}, statusSignalColor=${this.getStatusIndicatorColorClass(status)}`);

				//set the color of the status indicator
				this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass(status));

				if (status === 'ready') {
					//hysplit results are available
					//console.log('Fetching results')
					let url = `/${this.inspectionContainer.getApplication().getDomain()}/observation/inspection/hysplit/${this.type()}/${datum.id}/${callbackData.result.id}`;
					try {
						const response = await xhrPromise(
							{
								url: url,
								method: Method.get,
								accept: 'application/json',
								responseType: ResponseType.json,
								digester: this.inspectionContainer.getApplication().getDigester()
							}
						);
						let hysplitResult = response.data;
						this.setHysplitResult(hysplitResult);
						if (!hysplitResult) {
							//no content 
							status = 'retry';
							callbackData.retry = `/${this.inspectionContainer.getApplication().getDomain()}/observation/callback/hysplit/${datum.id}?retry=true`;
							this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass(status));
						}
					} catch (error) {
						if (status === originalStatus) {
							//unable to load the data
							this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass('undefined'));//maybe use another color here
							console.log('An error occured while fetching Hysplit result for project %s from %s. Next time we select the observation we might succeed.', callbackData.project, url, error);
						} else {
							//set state to retry or calculated so we can signal the server to ,...well...retry the calculation or retry the downloading of the results from the HPC
							status = originalStatus === 'fetching' ? 'calculated' : 'retry';
							this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass(status));
						}
					}
				}

				if (status !== 'ready') {
					//console.log('NOT fetching results')
					if (status === 'retry' || status === 'calculated') {
						//we need to trigger the server in retrying the calculation or the downloading of the results from the HPC
						console.log('Retry Hysplit callback %s due to state %s.', callbackData.retry, status);
						try {
							const response = await xhrPromise(
								{
									url: callbackData.retry,
									method: Method.get,
									accept: 'application/json',
									responseType: ResponseType.json,
									digester: this.inspectionContainer.getApplication().getDigester()
								}
							);
							let hysplitResponse: any | { status: string, project: string } = response.data;
							if (!!hysplitResponse.status) {
								callbackData.status = hysplitResponse.status;
								this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass(callbackData.status));
							} else {
								if (!hysplitResponse) {
									this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass(undefined));
								} else {
									console.log('Expected status aftertriggering a  hysplit retry. Got %s', hysplitResponse);
									this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass('ready'));
									this.setHysplitResult(hysplitResponse);
								}
							}

						} catch (error) {
							console.log('An error occured while initiating Hysplit.', callbackData, error);
							this.controls[this.type()].setEnabled(this.key(), true, this.getStatusIndicatorColorClass(undefined));
						}
					} else {
						//either still running or error
						if (status === 'error-model-run') {
							console.log('Hysplit callback for project %s resulted in a calculation error.', callbackData.project);
						} else if (status.startsWith('error')) {
							console.log('Hysplit callback for project %s not properly configured.', callbackData.project);
						} else {
							console.log('Hysplit callback for project %s still running.', callbackData.project);
						}
					}
				}
			} else {
				//hysplit visualization is not active
				this.controls[this.type()].setEnabled(this.key(), false, this.getStatusIndicatorColorClass(undefined));
				this.setHysplitResult(undefined);
			}
		} else {
			this.controls[this.type()].setEnabled(this.key(), this.controls[this.type()].enabled(this.key()), this.getStatusIndicatorColorClass(undefined));
			this.setHysplitResult(undefined);
		}
	}

	private getStatusIndicatorColorClass(status: string): string {
		if (!!status) {
			if (status === 'ready') {
				return 'tlmd-green';
			} else if (status === 'calculated') {
				return 'tlmd-yellow';
			} else if (status === 'retry') {
				return 'tlmd-orange';
			} else if (status.startsWith('error')) {
				return 'tlmd-red';
			} else {
				return 'tlmd-blue';
			}
		} else {
			return 'tlmd-gray';
		}
	}



	private subscribeHandlers(): void {
		let sectionElement = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.hysplit');
		if (sectionElement) {

			let formDiv = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.settings div.tlmd-foldout.hysplit-viewer');

			let thresholdsDiv = <HTMLElement>formDiv.querySelector('div.hysplit_thresholds');
			let thresholdEditorDiv = <HTMLElement>thresholdsDiv.querySelector('div.threshold_edit');
			let thresholdValueInput = <HTMLInputElement>thresholdEditorDiv.querySelector('span.value input');
			let thresholdHueInput = <HTMLInputElement>thresholdEditorDiv.querySelector('span.hue input');
			let thresholdHueSpan = <HTMLSpanElement>thresholdEditorDiv.querySelector('span.hue span.color');

			let thresholdsClicks = eventObserver(thresholdsDiv, 'click', { passive: true });
			let formChanges = eventObserver(formDiv, 'change', { passive: true });

			this.inspectionContainer.addSubscription(thresholdsClicks
				.pipe(
					filter(event => !parent(<HTMLElement>event.target, 'div.hysplit-analysis-legend') && !(<HTMLElement>event.target).closest('div.threshold_edit'))
				)
				.subscribe(_event => thresholdEditorDiv.style.display = 'none'));


			this.inspectionContainer.addSubscription(delegateObserver(thresholdsDiv, thresholdsClicks, 'div.hysplit-analysis-legend > div')
				.subscribe(evAndTrgt => {
					let isHighest = evAndTrgt.target.classList.contains('high');
					let modify = evAndTrgt.target.classList.contains('color');

					thresholdEditorDiv.classList.toggle('high', isHighest);
					thresholdEditorDiv.style.display = 'block';

					let value = modify ? evAndTrgt.target.dataset.value : undefined;
					let hue = modify ? evAndTrgt.target.dataset.hue : undefined;
					let backgroundColor = modify ? colorToString(getPaletteColor(0, parseFloat(evAndTrgt.target.dataset.hue))) : colorToString(getPaletteColor(0, 200.0));

					thresholdValueInput.value = value ? value : '';
					thresholdValueInput.dataset.source = value;

					thresholdHueInput.value = hue ? hue : '';
					thresholdHueInput.dataset.source = value;//yes indeed : NOT hue but value

					thresholdHueSpan.style.backgroundColor = backgroundColor;
				}));

			this.inspectionContainer.addSubscription(delegateObserver(thresholdsDiv, thresholdsClicks, 'div.threshold_edit div.remove')
				.subscribe(_evAndTrgt => {
					//remove the thresholds from the legend
					let value = thresholdValueInput.value;
					(<HTMLDivElement[]>Array.from(thresholdsDiv.querySelectorAll('div.hysplit-analysis-legend>div.color')))
						.filter(colorDiv => colorDiv.dataset.value === value)
						.forEach(colorDiv => colorDiv.parentNode.removeChild(colorDiv));
					//hide the threshold editor
					thresholdEditorDiv.style.display = 'none';
					//update the hysplit options and update the viewer
					if (this.hysplitViewer.getHysplit()) {
						this.hysplitViewer.getHysplit().options.heightClasses = this.refreshHeightClasses(formDiv);
						this.hysplitViewer.refresh();
					} else {
						this.refreshHeightClasses(formDiv);
					}
				}));

			this.inspectionContainer.addSubscription(delegateObserver(thresholdsDiv, formChanges, 'div.threshold_edit input')
				.subscribe(evAndTrgt => {
					let input = <HTMLInputElement>evAndTrgt.target;
					let threshold: HTMLDivElement;

					if (!parent(parent(input, 'span'), 'div.high')) {

						threshold = (<HTMLDivElement[]>Array.from(thresholdsDiv.querySelectorAll('div.hysplit-analysis-legend>div.color')))
							.filter(colorDiv => colorDiv.dataset.value === input.dataset.source && !colorDiv.classList.contains('high'))
							.reduce((firstThreshold, colorDiv) => firstThreshold ? firstThreshold : colorDiv, undefined);

					} else {
						threshold = thresholdsDiv.querySelector('div.hysplit-analysis-legend>div.color.high');
					}

					if (parent(input, 'span').classList.contains('hue')) {//a hue change

						let newHue = Number.parseFloat(input.value);
						if (!Number.isNaN(newHue)) {
							newHue = newHue % 360.0;
							thresholdHueSpan.style.backgroundColor = `${colorToString(getPaletteColor(0, newHue))}`;
							thresholdHueInput.value = newHue.toString();
							if (threshold) {//an existing threshold so update the hysplit options and update the viewer
								threshold.dataset.hue = newHue.toString();
								if (this.hysplitViewer.getHysplit()) {
									this.hysplitViewer.getHysplit().options.heightClasses = this.refreshHeightClasses(formDiv);
									this.hysplitViewer.refresh();
								} else {
									this.refreshHeightClasses(formDiv);
								}
							}
						}

					} else {//a value change

						let newValue = Number.parseFloat(input.value);
						if (!Number.isNaN(newValue)) {
							if (threshold) {//an existing threshold
								threshold.dataset.value = newValue.toString();
							} else {//a new threshold
								let newHue = this.getHueSuggestion(newValue, this.hysplitViewer.getHysplit() ? this.hysplitViewer.getHysplit().options.heightClasses : undefined);
								thresholdHueSpan.style.backgroundColor = `${colorToString(getPaletteColor(0, newHue))}`;
								thresholdHueInput.value = newHue.toString();

								let thresholdsMarkup = `<div class="color" data-hue="${newHue}" data-value="${newValue}" style="background-color:${colorToString(getPaletteColor(0, newHue))};">=&lt; ${newValue} m</div>`;
								thresholdsDiv.querySelector('div.hysplit-analysis-legend').insertAdjacentHTML('afterbegin', thresholdsMarkup);
							}

							thresholdHueInput.dataset.source = newValue.toString();
							thresholdValueInput.dataset.source = newValue.toString();

							//update the hysplit options and update the viewer
							if (this.hysplitViewer.getHysplit()) {
								this.hysplitViewer.getHysplit().options.heightClasses = this.refreshHeightClasses(formDiv);
								this.hysplitViewer.refresh();
							} else {
								this.refreshHeightClasses(formDiv);
							}
						}
					}
				}));

			this.inspectionContainer.addSubscription(delegateObserver(formDiv, formChanges, 'input,select')
				.pipe(filter(evAndTrgt => !evAndTrgt.target.closest('div.threshold_edit')))
				.subscribe(_evAndTrgt => {
					if (this.hysplitViewer.getHysplit()) {
						let hysplitOptions = this.hysplitViewer.getHysplit().options;
						hysplitOptions.main = Number.parseInt((<HTMLSelectElement>formDiv.querySelector('select.hysplit_main')).value);
						let analyze = (<HTMLSelectElement>formDiv.querySelector('select.hysplit_analyze')).value;
						hysplitOptions.analyze = analyze === 'main' || analyze === 'all' ? analyze : Number.parseInt(analyze);

						hysplitOptions.show.map.contour = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_contour')).checked;
						hysplitOptions.show.map.main = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_main')).checked;
						hysplitOptions.show.map.analyzed = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_analyzed')).checked;
						hysplitOptions.show.map.alternatives = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_alternatives')).checked;
						hysplitOptions.show.timeline.main = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_timeline_main')).checked;
						hysplitOptions.show.timeline.analyzed = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_timeline_analyzed')).checked;
						hysplitOptions.show.timeline.alternatives = (<HTMLInputElement>formDiv.querySelector('input.hysplit_show_timeline_alternatives')).checked;
						hysplitOptions.trace.map.main = (<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_map_main')).checked;
						hysplitOptions.trace.map.analyzed = (<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_map_analyzed')).checked;
						hysplitOptions.trace.map.alternatives = (<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_map_alternatives')).checked;
						hysplitOptions.trace.timeline.main = (<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_timeline_main')).checked;
						hysplitOptions.trace.timeline.analyzed = (<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_timeline_analyzed')).checked;
						hysplitOptions.trace.timeline.alternatives = (<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_timeline_alternatives')).checked;

						this.hysplitViewer.refresh();
					}
				}));
		}
	}

	private setHysplitResult(hysplitResult: HysplitResult): void {
		if (hysplitResult) {
			if (!this.hysplitViewer.getMap()) {
				this.hysplitViewer.setMap(this.source.getMap());
			}
			if (!this.hysplitViewer.getTimeline()) {
				this.hysplitViewer.setTimeline(this.source.getTimeline());
			}
			this.hysplitViewer.setHysplit(new Hysplit(hysplitResult, this.callbackDefinition.renderOptions, this.callbackDefinition.parameters.translation))
		} else {

			this.hysplitViewer.setMap(undefined);
			this.hysplitViewer.setTimeline(undefined);
			this.hysplitViewer.setHysplit(undefined)
		}
		this.updateSection();
	}

	private updateSection(): void {
		let sectionElement = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.hysplit');
		if (sectionElement) {
			let propertiesDiv = <HTMLDivElement>sectionElement.querySelector('div.properties');
			if (this.hysplitViewer.getHysplit()) {
				let hysplitProperties = this.hysplitViewer.getHysplit().properties;
				let hysplitOptions = this.hysplitViewer.getHysplit().options;

				(<HTMLSpanElement>propertiesDiv.querySelector('span.id')).innerText = hysplitProperties.id;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.result')).innerText = hysplitProperties.result;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.direction')).innerText = hysplitProperties.direction;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.runLength')).innerText = hysplitProperties.runLength;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.startTime')).innerText = hysplitProperties.startTime;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.startHeight')).innerText = hysplitProperties.startHeight;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.startLocation')).innerText = hysplitProperties.startLocation;
				(<HTMLSpanElement>propertiesDiv.querySelector('span.trajectoryCount')).innerText = hysplitProperties.trajectoryCount.toString();


				let formDiv = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.settings div.tlmd-foldout.hysplit-viewer');
				let domParser: DOMParser = new DOMParser();

				//fill main trajectory selector
				let mainSelect = <HTMLSelectElement>formDiv.querySelector('select.hysplit_main');
				mainSelect.innerHTML = '';
				[...Array(hysplitProperties.trajectoryCount).keys()]
					.map(i => {
						let html = `<option value="${i}"${hysplitOptions.main === i ? ' selected="selected"' : ''}>${i + 1}</option>`;
						return <HTMLOptionElement>domParser.parseFromString(html, 'text/html').body.firstChild;
					})
					.forEach(option => mainSelect.add(option));

				//fill analyze trajectory selector
				let analyzeSelect = <HTMLSelectElement>formDiv.querySelector('select.hysplit_analyze');
				analyzeSelect.innerHTML = `<option value="-1"${hysplitOptions.analyze === undefined || hysplitOptions.analyze < 0 ? ' selected="selected"' : ''}>&mdash;</option>
                                         <option value="main"${hysplitOptions.analyze === 'main' ? ' selected="selected"' : ''}>${this.callbackDefinition.labels.main}</option>
                                         <option value="all"${hysplitOptions.analyze === 'all' || hysplitOptions.analyze >= hysplitProperties.trajectoryCount ? ' selected="selected"' : ''}>*</option>`;
				[...Array(hysplitProperties.trajectoryCount).keys()]
					.map(i => {
						let html = `<option value="${i}"${hysplitOptions.analyze === i ? ' selected="selected"' : ''}>${i + 1}</option>`;
						return <HTMLOptionElement>domParser.parseFromString(html, 'text/html').body.firstChild;
					})
					.forEach(option => analyzeSelect.add(option));

				//set the various checkboxes
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_contour')).checked = hysplitOptions.show.map.contour;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_main')).checked = hysplitOptions.show.map.main;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_analyzed')).checked = hysplitOptions.show.map.analyzed;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_map_alternatives')).checked = hysplitOptions.show.map.alternatives;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_timeline_main')).checked = hysplitOptions.show.timeline.main;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_timeline_analyzed')).checked = hysplitOptions.show.timeline.analyzed;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_show_timeline_alternatives')).checked = hysplitOptions.show.timeline.alternatives;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_map_main')).checked = hysplitOptions.trace.map.main;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_map_analyzed')).checked = hysplitOptions.trace.map.analyzed;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_map_alternatives')).checked = hysplitOptions.trace.map.alternatives;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_timeline_main')).checked = hysplitOptions.trace.timeline.main;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_timeline_analyzed')).checked = hysplitOptions.trace.timeline.analyzed;
				(<HTMLInputElement>formDiv.querySelector('input.hysplit_animate_timeline_alternatives')).checked = hysplitOptions.trace.timeline.alternatives;

				this.readHeightClasses(formDiv, hysplitOptions.heightClasses);

				//show legend in info section
				let infoLegend = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.info div.tlmd-legend-item.hysplit');
				if (infoLegend) {
					infoLegend.classList.remove('tlmd-hide');
				}

			} else {
				//no hysplit data available.
				(<HTMLSpanElement>propertiesDiv.querySelector('span.id')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.model')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.timestep')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.result')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.direction')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.runLength')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.grid')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.effectiveHeight')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.diameter')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.exitSpeed')).innerText = '';
				//        (<HTMLSpanElement>propertiesDiv.querySelector('span.exitTemp')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.startTime')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.startHeight')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.startLocation')).innerText = '';
				(<HTMLSpanElement>propertiesDiv.querySelector('span.trajectoryCount')).innerText = '';

				//hide legend in info section
				let infoLegend = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.info div.tlmd-legend-item.hysplit');
				if (infoLegend) {
					infoLegend.classList.add('tlmd-hide');
				}

			}
		}
	}

	private writeHeightClasses(formDiv: HTMLDivElement): ValueClassification {
		const WIDTH = 0.75;

		let heightClassesNew: ValueClassification = {
			thresholds:
				(<HTMLDivElement[]>Array.from(formDiv.querySelectorAll('div.hysplit_thresholds div.hysplit-analysis-legend>div.color')))
					.filter(colorDiv => !colorDiv.classList.contains('high'))
					.map(colorDiv => {
						let hue = Number.parseFloat(colorDiv.dataset.hue);
						let value = Number.parseFloat(colorDiv.dataset.value);
						let color = getPaletteColor(0, hue);
						return { value: value, stroke: { color: { hue: color.hue, saturation: color.saturation / 100, lightness: color.lightness / 100, alpha: color.alpha }, width: WIDTH } };
					})
					.sort((ta, tb) => ta.value - tb.value),

			highStroke:
				(<HTMLDivElement[]>Array.from(formDiv.querySelectorAll('div.hysplit_thresholds div.hysplit-analysis-legend>div.color.high')))
					.map(colorDiv => {
						let hue = Number.parseFloat(colorDiv.dataset.hue);
						let color = getPaletteColor(0, hue);
						return { color: { hue: color.hue, saturation: color.saturation / 100, lightness: color.lightness / 100, alpha: color.alpha }, width: WIDTH };
					})[0]
		};

		console.log(heightClassesNew);
		return heightClassesNew;
	}

	private buildInnerLegend(heightClasses: ValueClassification): string {
		let innerLegendMarkup = '';
		if (heightClasses) {
			innerLegendMarkup += heightClasses.thresholds
				.map(threshold =>
					`<div class="color" data-hue="${threshold.stroke.color.hue}" data-value="${threshold.value}" style="background-color:${colorToString(getPaletteColor(0, threshold.stroke.color.hue))};">=&lt; ${threshold.value} m</div>`
				)
				.reduce((markup, thresholdMarkup) => markup + thresholdMarkup, '');

			let highStrokeValue = heightClasses.thresholds.length > 0 ? heightClasses.thresholds[heightClasses.thresholds.length - 1].value : undefined;
			let highStrokeHue = heightClasses.highStroke.color.hue;
			let highStrokeColor = getPaletteColor(0, highStrokeHue);
			innerLegendMarkup += `<div class="color high" data-hue="${highStrokeHue}" style="background-color:${colorToString(highStrokeColor)};">${highStrokeValue !== undefined ? `&gt; ${highStrokeValue} m` : '&nbsp;'}</div>`;
		}
		return innerLegendMarkup;
	}

	private readHeightClasses(formDiv: HTMLDivElement, heightClasses: ValueClassification): ValueClassification {
		let innerLegendMarkup = this.buildInnerLegend(heightClasses);
		(<HTMLDivElement>formDiv.querySelector('div.hysplit_thresholds div.hysplit-analysis-legend')).innerHTML = innerLegendMarkup + '<div class="add tlmd-button btn tlmd-small tlmd-default"><span class="icon-plus"></span></div>';

		let infoLegendContainer = <HTMLDivElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.info div.tlmd-legend-item.hysplit div.hysplit-analysis-legend');
		if (infoLegendContainer) {
			infoLegendContainer.innerHTML = innerLegendMarkup;
		}
		return heightClasses;
	}

	private refreshHeightClasses(formDiv: HTMLDivElement): ValueClassification {
		return this.readHeightClasses(formDiv, this.writeHeightClasses(formDiv));
	}

	private getHueSuggestion(value: number, heightClasses: ValueClassification): number {
		if (heightClasses && heightClasses.thresholds.length > 0) {
			let i = 0;
			while (i < heightClasses.thresholds.length && heightClasses.thresholds[i].value < value) i++;
			if (i === 0) return heightClasses.thresholds[i].stroke.color.hue;
			let h0, h1;
			if (i >= heightClasses.thresholds.length) {
				h0 = heightClasses.thresholds[heightClasses.thresholds.length - 1].stroke.color.hue;
				h1 = heightClasses.highStroke.color.hue;
			} else {
				h0 = heightClasses.thresholds[i - 1].stroke.color.hue;
				h1 = heightClasses.thresholds[i].stroke.color.hue;
			}
			return (h0 + h1) / 2.0;
		} else {
			return 7;
		}
	}

}